import {
	Button,
	createMuiTheme,
	MuiThemeProvider,
	TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	deleteCountry,
	getCountryById,
	postCountry,
	updateCountry,
} from "../../../../api/country";
import { postLog } from "../../../../api/log";
import { LOGS } from "../../../../utils/constants";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyCountry() {
	return {
		name: "",
	};
}

export default function EditCountriesPage() {
	const [country, setCountry] = useState(getEmptyCountry());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

	const countryId = useParams().id;
	const history = useHistory();
	const location = useLocation();

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!countryId) {
			disableLoadingData();
			return;
		}
		getCountryById(countryId)
			.then((res) => {
				if (res.status === 200) {
					setCountry(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get country.",
				});
				handleBack();
			});
	}, [countryId, disableLoadingData, history]);

	const handleBack = () => {
		const prevTablePage = location?.state?.page;

		history.push(
			`/countries${prevTablePage ? "?page=" + prevTablePage : ""}`
		);
	};

	function saveCountry() {
		if (!countryId) {
			postCountry(country)
				.then((res) => {
					if (res.status === 201) {
						postLog(
							loggedUser,
							LOGS.FIELDS.COUNTRIES,
							`${LOGS.ACTIONS.CREATE}-${country?.name}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage: "Country created successfully.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save country.",
					});
				});
		} else {
			updateCountry(countryId, country)
				.then((res) => {
					if (res.status === 200) {
						postLog(
							loggedUser,
							LOGS.FIELDS.COUNTRIES,
							`${LOGS.ACTIONS.UPDATE}-${country?.name}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setCountry({ ...country, [element]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit country"></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label="Name"
							value={country.name}
							onChange={handleChange("name")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
					</CardBody>
				</Card>
				<Button
					onClick={handleBack}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Back
				</Button>
				<Button
					onClick={() => saveCountry()}
					variant="outlined"
					color="primary"
					style={countryId && { marginRight: "20px" }}
				>
					Save country
				</Button>
				{countryId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								className="py-2"
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Delete country
							</Button>
						</MuiThemeProvider>
						<ConfirmDialog
							title={
								"Are you sure you want to delete this country?"
							}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteCountry(countryId)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											postLog(
												loggedUser,
												LOGS.FIELDS.COUNTRIES,
												`${LOGS.ACTIONS.DELETE}-${country?.name}`
											);
											alertSuccess({
												title: "Deleted!",
												customMessage:
													"Country deleted successfully.",
											});
											handleBack();
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"Could not delete country.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
